import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import BOLayout from '@/pages/back-office/BOLayout';
import Dropdown from '@/components/Dropdown';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import ProductsImportModal from '@/components/back-office/ProductsImportModal';

const BORestaurant = () => {
	const [name, setName] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [isNoLimit, setIsNoLimit] = useState(false);
	const [isNoLimitType, setIsNoLimitType] = useState('normal');
	const [isNoLimitMinimumAmount, setIsNoLimitMinimumAmount] = useState(0);
	const [importProducts, setImportProducts] = useState(false);
	const [importMenus, setImportMenu] = useState(false);
	const [importStocks, setImportStocks] = useState(false);
	const [canSubmit, setCanSubmit] = useState(false);
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('');
	const { restaurants } = useRestaurantsContext();
	const navigate = useNavigate();
	const role = JSON.parse(localStorage.getItem('role'));
	const [showProductsModal, setShowProductsModal] = useState(false);
	const [selectedProducts, setSelectedProducts] = useState([]);

	const NO_LIMIT_TYPES = {
		list: 'Toutes les catégories',
		category: 'Catégorie spécifique',
	};

	useEffect(() => {
		if (!['super-admin'].includes(role)) {
			toast.info("Vous n'avez pas les privilèges pour accéder à cette page", {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office'), 500;
		}
	}, []);

	useEffect(() => {
		if (NO_LIMIT_TYPES[isNoLimitType] && categories.length === 0) {
			const fetchCategories = async () => {
				try {
					const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);
					if (response.status === 200) {
						setCategories(response.data);
					}
				} catch (error) {
					console.error('Error fetching categories:', error);
					toast.error('Une erreur est survenue', {
						position: 'top-center',
						autoClose: 3500,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						theme: 'colored',
					});
				}
			};

			fetchCategories();
		} else {
			if (isNoLimitType === 'list' && categories.length > 0) {
				setSelectedCategory('');
			}
		}
	}, [isNoLimitType]);

	useEffect(() => {
		const checkCanSubmit = () => {
			if (
				!name ||
				!startDate ||
				(isNoLimit &&
					(!Object.keys(NO_LIMIT_TYPES).includes(isNoLimitType) ||
						(Object.keys(NO_LIMIT_TYPES).includes(isNoLimitType) && isNoLimitMinimumAmount <= 0) ||
						(Object.keys(NO_LIMIT_TYPES).includes(isNoLimitType) && isNoLimitType === 'category' && (!selectedCategory || isNoLimitMinimumAmount <= 0))))
			) {
				setCanSubmit(false);
				return;
			}

			setCanSubmit(true);
		};

		checkCanSubmit();
	}, [name, startDate, isNoLimit, isNoLimitType, selectedCategory, isNoLimitMinimumAmount]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		const formData = new FormData();
		formData.append('name', name.toLowerCase().trim().split(' ').join('-'));
		formData.append('start_date', startDate);
		formData.append('end_date', endDate);
		formData.append('role', JSON.parse(localStorage.getItem('role')));
		formData.append('owner_email', JSON.parse(localStorage.getItem('user'))?.email);
		formData.append('is_no_limit', isNoLimit);
		formData.append('is_no_limit_type', isNoLimitType);
		formData.append('is_no_limit_minimum_amount', isNoLimitMinimumAmount);
		formData.append('is_no_limit_selected_category', JSON.stringify(selectedCategory));
		formData.append('import_products', importProducts);
		formData.append('import_menus', importMenus);
		formData.append('import_stocks', importStocks);
		formData.append('selected_products', JSON.stringify(selectedProducts));

		const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/creation`, formData);

		if (response.status === 201) {
			toast.success('Création du restaurant réussie', {
				position: 'top-center',
				autoClose: 800,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		} else {
			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 800,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		setTimeout(() => (window.location.href = `/back-office`), 1000);
	};

	const handleImportProductsChange = (e) => {
		setImportProducts(e.target.checked);
		if (e.target.checked) {
			setShowProductsModal(true);
		} else {
			setSelectedProducts([]);
		}
	};

	const handleProductsSelection = (products) => {
		setSelectedProducts(products);
	};

	// Get today's date in YYYY-MM-DD format
	const today = new Date().toISOString().split('T')[0];

	return (
		<BOLayout>
			<form>
				<div className="flex flex-col mb-8">
					<label htmlFor="name" className="mb-2 px-4">
						Nom du restaurant <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						id="name"
						name="name"
						onChange={(e) => setName(e.target.value)}
						className="rounded-full p-2 px-4 border border-black shadow-md w-1/5"
						required
					/>
				</div>

				<div className="flex gap-24 mb-4">
					<div className="flex flex-col">
						<label htmlFor="address" className="mb-2 px-4">
							Date de début <span className="text-red-500">*</span>
						</label>
						<input
							type="date"
							id="start_date"
							name="start_date"
							onChange={(e) => setStartDate(e.target.value)}
							className="rounded-full p-2 px-4 border border-black shadow-md"
							min={today}
							required
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="address" className="mb-2 px-4">
							Date de fin
						</label>
						<input
							type="date"
							id="end_date"
							name="end_date"
							onChange={(e) => setEndDate(e.target.value)}
							className="rounded-full p-2 px-4 border border-black shadow-md"
							min={startDate || today}
							required
						/>
					</div>
				</div>

				<div className="mb-12">
					<div className="flex flex-col justify-center mb-4">
						<div className="mb-2">
							<label htmlFor="is_no_limit">
								Autoriser la commande libre ? <span className="text-red-500">*</span>
							</label>
							<div className="flex items-center">
								<div className="flex items-center gap-2 ml-4">
									<div className="flex items-center">
										<div className="flex items-center gap-2 ml-4" onClick={() => setIsNoLimit(true)}>
											<input
												type="radio"
												name="is_no_limit"
												id="is_no_limit_yes"
												checked={isNoLimit}
												onChange={() => setIsNoLimit(true)}
											/>
											<span>Oui</span>
										</div>
										<div className="flex items-center gap-2 ml-4" onClick={() => setIsNoLimit(false)}>
											<input
												type="radio"
												name="is_no_limit"
												id="is_no_limit_no"
												checked={!isNoLimit}
												onChange={() => setIsNoLimit(false)}
											/>
											<span>Non</span>
										</div>
									</div>
								</div>
							</div>
						</div>

						{isNoLimit && (
							<div className="mb-2">
								<label htmlFor="is_no_limit">
									Quel est le type de cette commande libre ? <span className="text-red-500">*</span>
								</label>
								<div className="flex items-center">
									<div className="flex items-center gap-2 ml-4">
										<div className="flex items-center">
											<div className="flex items-center gap-2 ml-4">
												{Object.entries(NO_LIMIT_TYPES).map((type) => {
													const [key, value] = type;

													return (
														<div key={key} onClick={() => setIsNoLimitType(key)}>
															<input
																className="mr-2"
																type="radio"
																name="is_no_limit_type"
																value={key}
																id={`is_no_limit_${key}`}
																checked={isNoLimitType === key}
																onChange={() => setIsNoLimitType(key)}
															/>
															<span>{value}</span>
														</div>
													);
												})}
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{isNoLimit && (isNoLimitType === 'list' || isNoLimitType === 'category') && (
							<div className="mb-2 grid grid-cols-2 gap-8 w-3/5">
								{isNoLimitType === 'category' && (
									<div className="mb-2 flex flex-col">
										<label htmlFor="is_no_limit_minimum_amount" className="mb-2">
											Quel est la catégorie ciblée ?
										</label>
										<div className="w-full relative">
											<Dropdown
												buttonText={selectedCategory?.name || 'Choisissez une catégorie'}
												placeholder="Choisissez une catégorie"
												options={categories}
												keyToFind={'name'}
												isObject={true}
												classes={`p-2 border border-gray-300 rounded-md w-full flex justify-between items-center overflow-y-auto}`}
												onOptionSelect={setSelectedCategory}
											/>
										</div>
									</div>
								)}

								<div className="mb-2 flex flex-col">
									<label htmlFor="is_no_limit_minimum_amount" className="mb-2">
										Quel est le montant minimum ?
									</label>
									<input
										type="number"
										id="is_no_limit_minimum_amount"
										name="is_no_limit_minimum_amount"
										onChange={(e) => setIsNoLimitMinimumAmount(e.target.value)}
										className="rounded-full p-2 px-4 border border-black shadow-md"
									/>
								</div>
							</div>
						)}
					</div>

					{restaurants.length > 0 && (
						<>
							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									name="import_products"
									id="import_products"
									onChange={handleImportProductsChange}
									checked={importProducts}
								/>
								<span> Importer la liste des produits</span>
							</div>

							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									name="import_menus"
									id="import_menus"
									onChange={(e) => setImportMenu(e.target.checked)}
								/>
								<span> Importer la liste des formules</span>
							</div>

							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									name="import_stocks"
									id="import_stocks"
									onChange={(e) => setImportStocks(e.target.checked)}
								/>
								<span> Importer les stocks</span>
							</div>
						</>
					)}
				</div>

				<button
					type="submit"
					onClick={handleSubmit}
					className={`transition duration-300 text-white px-4 py-2 rounded-lg ${canSubmit
							? 'bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70'
							: 'bg-gray-400 cursor-not-allowed'
						}`}
					disabled={!canSubmit}
				>
					Créer le restaurant
				</button>
			</form>

			<ProductsImportModal
				isOpen={showProductsModal}
				onClose={() => setShowProductsModal(false)}
				onConfirm={handleProductsSelection}
				restaurantId={restaurants.length > 0 ? restaurants.find((r) => r.is_default).id : null}
			/>
		</BOLayout>
	);
};

export default BORestaurant;
